import * as fragments from "./fragments";

const isInt = (n) => Number(n) === n && n % 1 === 0;

export function number(value) {
    return value !== undefined ? Number(value) : undefined;
}

export function array(value) {
    return (value || "").split(",").map((v) => v.trim());
}

export function jsonFilter(filterString) {
    const jsonValue = {};
    const pairs = (filterString || "").split(",").filter((p) => p);

    pairs.forEach((pair) => {
        const [key, value] = pair.split(":");
        jsonValue[key] = decodeURIComponent(value);
    });
    return jsonValue;
}

export function searchFilter(json) {
    return Object.entries(json)
        .map(([key, value]) => `${key}:${value}`)
        .join(",");
}

export function parseFilter(filterString) {
    try {
        const s = decodeURIComponent(escape(window.atob(filterString)));
        return JSON.parse(s);
    } catch (_) {
        return {};
    }
}

export function stringifyFilter(filterString) {
    try {
        const s = unescape(encodeURIComponent(JSON.stringify(filterString)));
        return btoa(s);
    } catch (_) {
        return "";
    }
}

export function alias({ resource, args, locale, body }, appendBody, appendAll, isDatagridReq) {
    const aliasArgs = args || {};

    const argsString =
        resource !== "search"
            ? Object.entries(aliasArgs)
                  .filter(([k, v]) => k !== "page" && k !== "pageSize" && v)
                  .sort(([aK], [bK]) => aK.localeCompare(bK))
                  .map(
                      ([k, v]) =>
                          `${k}_${
                              Array.isArray(v)
                                  ? v
                                        .map((vv) =>
                                            typeof vv === "string" ? vv : JSON.stringify(vv),
                                        )
                                        .join("_")
                                  : JSON.stringify(v)
                          }`,
                  )
                  .join("_")
                  .normalize("NFD")
                  .replace(/-/g, "_")
                  .replace(/&/g, "e")
                  .replace(/\|/g, "o")
                  .replace(/[\u0300-\u036f]/g, "")
                  .replace(/[^\w ]+/g, "")
                  .replace(/ +/g, "_")
            : "";

    return `${locale ? `${locale}_` : ""}${resource}${argsString ? `_${argsString}` : ""}${
        appendBody && body ? `(${body})` : ""
    }${(appendAll || isDatagridReq) && (args.page || args.pageSize) ? `_pagging_${args.page}_${args.pageSize}` : ""}`;
}

const getRequiredFragements = (fragment, fragmentsList, download) => {
    const fragments = [];
    const { entity, body, xlsxExport } = fragmentsList[fragment] || {};

    const fragmentDependencies = (
        (download ? xlsxExport || body : body || "").match(/\.{3}[a-zA-Z0-9_]+/gm) || []
    ).map((f) => f.substring(3));

    fragmentDependencies.forEach((dependency) => {
        const innerFragments = getRequiredFragements(dependency, fragmentsList, download);
        fragments.push(...innerFragments);
    });
    if (
        entity &&
        (download ? xlsxExport || body : body) &&
        entity.length &&
        (download ? xlsxExport || body : body).length
    ) {
        fragments.push([
            fragment,
            `fragment ${fragment} on ${entity} {__typename,${
                download ? xlsxExport || body : body
            }}`,
        ]);
    }
    return fragments;
};

export function buildRequestBody(queries = [], locale, download = false) {
    if (!queries || !queries.length) {
        return null;
    }
    const variables = {};
    const variablesTypes = new Set();
    const queryFragments = {};
    const queriesString = [];
    const qq = queries || [];
    let increment = 0;
    qq.forEach((query) => {
        const args = query.args || {};
        const queryStringArgs = [];
        Object.entries(args).forEach(([key, argValue]) => {
            ++increment;
            const type = (argValue || {}).type
                ? argValue.type
                : Array.isArray(argValue)
                  ? "array"
                  : typeof argValue;
            const value = (argValue || {}).value ? argValue.value : argValue;
            // TODO: add type for FilterInput
            const tempKey =
                variables[key] && variables[key] !== value ? `${key}${increment}` : `${key}`;
            switch (type) {
                case "number":
                    if (value >= 0) {
                        const numberType = isInt(value) ? "Int" : "Float";
                        variablesTypes.add(`$${tempKey}: ${numberType}`);
                        variables[tempKey] = value;
                        queryStringArgs.push(`${key}:$${tempKey}`);
                    }
                    break;
                case "array": {
                    const innerType =
                        typeof value[0] === "number" ? (isInt(value) ? "Int" : "Float") : "String";
                    variablesTypes.add(`$${tempKey}: [${innerType}]`);
                    variables[tempKey] = value;
                    queryStringArgs.push(`${key}:$${tempKey}`);
                    break;
                }
                case "object":
                    variablesTypes.add(`$${tempKey}: JSON`);
                    variables[tempKey] = value;
                    queryStringArgs.push(`${key}:$${tempKey}`);
                    break;
                case "boolean":
                    variablesTypes.add(`$${tempKey}: Boolean`);
                    variables[tempKey] = value;
                    queryStringArgs.push(`${key}:$${tempKey}`);
                    break;
                case "undefined":
                    break;
                default:
                    variablesTypes.add(`$${tempKey}: ${type !== "string" ? type : "String"}`);
                    variables[tempKey] = value;
                    queryStringArgs.push(`${key}:$${tempKey}`);
                    break;
            }
        });

        const isDatagridReq = query.isDatagridReq;
        const queryAlias = alias(
            {
                resource: query.resource,
                args: query.args,
                locale,
            },
            null,
            null,
            isDatagridReq,
        );
        const queryString = `${queryAlias}:${query.resource}${
            queryStringArgs.length ? `(${queryStringArgs.join(",")})` : ""
        }${query.body ? `{${query.body}}` : ""}`;
        queriesString.push(queryString);

        if (query.body) {
            const foundFragments = query.body.match(/\.{3}[a-zA-Z0-9_]+/gm);
            if (foundFragments) {
                foundFragments.forEach((fragment) => {
                    const fragmentName = fragment.substring(3);
                    const requiredFragments = getRequiredFragements(
                        fragmentName,
                        fragments,
                        download,
                    );
                    requiredFragments.forEach(([name, body]) => {
                        if (!queryFragments[name]) {
                            queryFragments[name] = body;
                        }
                    });
                });
            }
        }

        if (query && query.resource) {
            variables.entityName = query.resource;
        }
        if (query?.downloadFilePrefixName) {
            variables.downloadFilePrefixName = query.downloadFilePrefixName || "";
        }

    });

    const query = `${[...Object.values(queryFragments)].join("\n")}query${
        variablesTypes.size ? `( ${[...variablesTypes].join(",")} )` : ""
    }{${queriesString.join()}}`;
    return {
        query,
        variables,
    };
}

// Async wrapper to fetch DB xlsx labels for queries with xlsxTranslation
export async function buildRequestBodyAsync(queries = [], locale, download = false, fetchApi) {
    const queriesClone = (queries || []).map((q) => ({ ...q, args: { ...(q.args || {}) } }));
    // Collect unique resources to fetch
    const resources = Array.from(
        new Set(
            queriesClone
                .map((q) => q.xlsxTranslation)
                .filter((r) => typeof r === "string" && r.trim().length > 0),
        ),
    );

    const labelsByResource = {};
    if (resources.length) {
        // Build GraphQL batch request to fetch labels per resource
        const parts = resources.map(
            (r, i) => `r${i}:xlsx_labels(resource:"${r}", lang:"${locale}"){labels}`,
        );
        const labelsQuery = `query{${parts.join(" ")}}`;
        const body = { query: labelsQuery };
        try {
            const res = await fetchApi("/graphql", body, { headers: { "Content-Type": "application/json" } });
            const json = await res.json().catch(() => ({}));
            const data = json.data || {};
            resources.forEach((r, i) => {
                const key = `r${i}`;
                labelsByResource[r] = (data[key] || {}).labels || null;
            });
        } catch (e) {
            console.warn("[xlsx_labels] Failed to fetch labels", {
                resources,
                locale,
                error: (e && (e.message || e.toString())) || "unknown",
            });
        }
    }

    // Build base body and attach a top-level xlsxLabels variable (not as a field argument)
    const base = buildRequestBody(queriesClone, locale, download);
    if (resources.length === 1) {
        const r = resources[0];
        if (labelsByResource[r]) {
            base.variables.xlsxLabels = labelsByResource[r];
        }
    }
    return base;
}

export function checkForCustomFragment(modulePrefix, resource) {
    const entityResource = modulePrefix ? `${modulePrefix}_${resource}` : resource;
    let customFragment = false;

    if (entityResource && fragments && fragments[entityResource]) {
        customFragment = true;
    }

    return customFragment;
}

export const camelizeResource = (str) => {
    return (str || "").replace(/_([a-z])/g, (v) => v[1].toUpperCase());
};

export const getEntityNameResource = (entity) => {
    return (entity || "").substring(0, (entity || "").length - 1);
};

export const validateEmail = (email) => {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        );
};
